// PracticeDetailPage.js
// Một bộ đề luyện thi. Đề thi dành cho học viên trung tâm: chưa mở khoá (SĐT phụ huynh + tên) thì máy chủ
// không gửi nội dung đề (practice.contentLocked), bấm vào đề sẽ hiện popup mở khoá.
// Học viên đã mở khoá (và admin) xem mọi đề, không cần dùng xu.
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Lock } from "lucide-react";
import PracticeExamDetail from "../components/PracticeExamDetail";
import { AppContext } from "../AppContext.js";
import { useLearner } from "../LearnerContext.js";
import { recordActivity } from "../learnerApi.js";
import SmartImg from "../components/SmartImg";

const PracticeDetailPage = () => {
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { fetchPracticeById } = useContext(AppContext);
  const { learner, openGate } = useLearner();

  // Tải lại khi mở khoá / thoát học viên
  useEffect(() => {
    let cancelled = false;
    const getPracticeData = async () => {
      try {
        const data = await fetchPracticeById(id);
        if (cancelled) return;
        if (data) {
          setPractice(data);
          setError(null);
        } else {
          setError("Không tìm thấy bộ đề luyện thi này.");
        }
      } catch (err) {
        console.error("Lỗi khi tải chi tiết bộ đề:", err);
        if (!cancelled) setError("Không thể tải chi tiết bộ đề. Vui lòng thử lại sau.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    getPracticeData();
    return () => {
      cancelled = true;
    };
  }, [id, fetchPracticeById, learner?.id]);

  const handleExamClick = (exam) => {
    if (practice.contentLocked) {
      openGate();
      return;
    }
    recordActivity({
      type: "open_practice",
      refKey: `practice:${practice.id}:${exam.id}`,
      title: `${practice.title} › ${exam.title}`,
    });
    setSelectedExam(exam);
  };

  const renderPracticeExams = (exams) => {
    if (!exams || exams.length === 0) {
      return <p className="text-gray-600">Chưa có đề thi nào trong bộ đề này.</p>;
    }

    return exams.map((exam) => (
      <div
        key={exam.id}
        onClick={() => handleExamClick(exam)}
        className="flex items-center justify-between p-3 mb-2 rounded-md shadow-sm transition-all duration-200 bg-white hover:bg-gray-100 hover:shadow-md cursor-pointer border border-gray-200"
      >
        <span className="text-gray-800 font-medium text-base">{exam.title}</span>
        <button className="flex ml-2 items-center justify-center text-sm font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0">
          {practice.contentLocked ? (
            <>
              <Lock size={15} className="mr-1 text-gray-500" /> Học viên
            </>
          ) : (
            <>
              <Edit size={16} className="mr-1" /> Xem
            </>
          )}
        </button>
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mx-auto mb-4"></div>
        <p className="text-xl text-gray-700">Đang tải dữ liệu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 p-8 text-center min-h-[calc(100vh-160px)] flex flex-col items-center justify-center">
        <strong className="font-bold">Lỗi!</strong>
        <span className="block sm:inline"> {error}</span>
        <button
          onClick={() => navigate("/")}
          className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-full shadow-md transition-all duration-300"
        >
          <ArrowLeft size={18} className="inline mr-2" />
          Quay lại Trang Chủ
        </button>
      </div>
    );
  }

  if (!practice) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-4 p-8 text-center min-h-[calc(100vh-160px)] flex flex-col items-center justify-center">
        <strong className="font-bold">Không tìm thấy!</strong>
        <span className="block sm:inline"> Không có dữ liệu bộ đề.</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {selectedExam ? (
        <PracticeExamDetail exam={selectedExam} onClose={() => setSelectedExam(null)} />
      ) : (
        <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-8 flex flex-col lg:flex-row items-start lg:space-x-10">
          <div className="lg:w-1/2 w-full mb-6 lg:mb-0">
            <SmartImg eager src={practice.image} alt={practice.title} className="w-full h-auto rounded-xl shadow-lg" />
          </div>
          <div className="lg:w-1/2 w-full">
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">{practice.title}</h2>
            <p className="text-gray-700 text-lg mb-6">{practice.description}</p>
            <div className="mt-8">
              <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Edit size={28} className="text-indigo-600 mr-3" />
                Danh sách đề thi
              </h3>
              {practice.contentLocked && (
                <p className="text-sm text-gray-600 bg-indigo-50 border border-indigo-100 rounded-md p-3 mb-3">
                  Đề thi dành cho học viên đang theo học tại trung tâm Mây Xanh. Bấm vào một đề để nhập số
                  điện thoại phụ huynh.
                </p>
              )}
              <div className="bg-gray-50 rounded-lg p-3 lg:p-4 shadow-inner">
                {renderPracticeExams(practice.practiceLists)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeDetailPage;
